import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ArrivalCity, CreateArrivalCityInput, PatchArrivalCityInput } from '@magimp/shared';

// WHY (#699): client-side gate for the shortName field mirrors the
// shared Zod regex (Cyrillic upper+lower range U+0410-U+044F plus
// U+0401/U+0451 plus Latin A-Z/a-z plus digits, 1-3 chars). Encoded
// as Unicode escapes so this file stays free of localized literals
// per the apps/web i18n linter contract. The shared schema in
// packages/shared/src/schemas/arrivalCities.ts uses the same
// character class with literal characters; both must stay in
// lockstep. The literal `[\u0410-\u042F\u0430-\u044F]` range covers `\u0410-\u042F`
// (\u0410-\u042F) plus `\u0430-\u044F` (\u0430-\u044F) and intentionally excludes
// `\u0401` (\u0401) / `\u0451` (\u0451), which fall outside both ranges.
const SHORT_NAME_REGEX = /^[\u0410-\u044F\u0430-\u044FA-Za-z0-9]{1,3}$/;
import {
  Button,
  DataTable,
  EditorModal,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  Select,
  ToolbarActions,
  useEditorGuard,
} from '../../../ui/index.js';
import {
  activateArrivalCity,
  archiveArrivalCity,
  createArrivalCity,
  listArrivalCities,
  patchArrivalCity,
} from '../../../api/arrival-cities.js';
import { errorMessage } from '../../../api/errors.js';
import { useToastStore } from '../../../stores/toast.js';
import { hasPermission, useAuthStore } from '../../../stores/auth.js';

type StatusFilter = 'active' | 'inactive' | 'all';

const ARRIVAL_CITIES_QK = ['arrival-cities-catalog'] as const;

export function ArrivalCitiesPage() {
  const { t } = useTranslation(['arrivalCities', 'common']);
  // WHY (#623): the route is gated on `arrivalCities.read` only; read-
  // only users would otherwise see Create / Edit / Archive / Restore
  // buttons whose POST/PATCH/DELETE returns 403. Hide them so the UI
  // matches server policy.
  const permissions = useAuthStore((s) => s.permissions);
  const canManage = hasPermission(permissions, 'arrivalCities.manage');
  const [status, setStatus] = useState<StatusFilter>('active');
  const [q, setQ] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [selected, setSelected] = useState<ArrivalCity | null>(null);

  const query = useQuery({
    queryKey: [...ARRIVAL_CITIES_QK, status, q],
    queryFn: () => listForFilter(status, q),
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0, flex: 1 }}>
      <header style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0, flex: 1 }}>
          {t('arrivalCities:title')}
        </h1>
        <ToolbarActions>
          <Input
            value={q}
            onChange={setQ}
            icon="search"
            placeholder={t('arrivalCities:search')}
            maxLength={200}
          />
          <Select
            value={status}
            onChange={(v) => setStatus(v as StatusFilter)}
            options={[
              { value: 'active', label: t('arrivalCities:filters.active') },
              { value: 'inactive', label: t('arrivalCities:filters.inactive') },
              { value: 'all', label: t('arrivalCities:filters.all') },
            ]}
            style={{ width: 150 }}
          />
          {canManage && (
            <Button kind="primary" icon="plus" onClick={() => setCreateOpen(true)}>
              {t('arrivalCities:actions.create')}
            </Button>
          )}
        </ToolbarActions>
      </header>
      {query.error && (
        <ErrorState
          title={t('common:states.loadError')}
          text={errorMessage(query.error)}
          onRetry={() => void query.refetch()}
        />
      )}
      {query.isPending && <LoadingState />}
      {query.isSuccess && (
        <DataTable
          rows={query.data}
          onSelect={setSelected}
          emptyState={
            <EmptyState title={t('common:states.emptyTitle')} text={t('arrivalCities:empty')} />
          }
          columns={[
            { key: 'name', label: t('arrivalCities:columns.name'), sortable: true },
            {
              key: 'shortName',
              label: t('arrivalCities:columns.shortName'),
              width: 110,
              sortable: true,
              sortValue: (row) => row.shortName,
              render: (row) => (
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{row.shortName}</span>
              ),
            },
            {
              key: 'default',
              label: t('arrivalCities:columns.default'),
              width: 110,
              sortable: true,
              sortValue: (row) => (row.isDefault ? 1 : 0),
              render: (row) =>
                row.isDefault ? (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '2px 8px',
                      borderRadius: 999,
                      fontSize: 11,
                      background: 'var(--status-info-bg)',
                      color: 'var(--status-info-fg)',
                    }}
                  >
                    {t('arrivalCities:badges.default')}
                  </span>
                ) : (
                  ''
                ),
            },
            {
              key: 'status',
              label: t('arrivalCities:columns.status'),
              width: 130,
              sortable: true,
              sortValue: (row) => (row.active ? 1 : 0),
              render: (row) =>
                row.active ? t('arrivalCities:status.active') : t('arrivalCities:status.inactive'),
            },
          ]}
        />
      )}
      {createOpen && canManage && <ArrivalCityCreateModal onClose={() => setCreateOpen(false)} />}
      {selected && (
        <ArrivalCityEditorModal
          row={selected}
          canManage={canManage}
          onClose={() => setSelected(null)}
          onUpdated={setSelected}
        />
      )}
    </div>
  );
}

async function listForFilter(status: StatusFilter, q: string): Promise<ArrivalCity[]> {
  const base: { q?: string; limit: number } = { limit: 100 };
  if (q.trim()) base.q = q.trim();
  if (status === 'active') return (await listArrivalCities({ ...base, active: true })).items;
  if (status === 'inactive') return (await listArrivalCities({ ...base, active: false })).items;
  const [active, inactive] = await Promise.all([
    listArrivalCities({ ...base, active: true }),
    listArrivalCities({ ...base, active: false }),
  ]);
  return [...active.items, ...inactive.items];
}

function ArrivalCityCreateModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation(['arrivalCities', 'common']);
  const qc = useQueryClient();
  const showToast = useToastStore((s) => s.show);
  const [name, setName] = useState('');
  const [shortName, setShortName] = useState('');
  const [isDefault, setIsDefault] = useState(false);

  // WHY (#699): the create payload is null whenever any required field
  // fails the wire schema (non-empty trimmed name + shortName matching
  // the 1-3 char regex). Returning null keeps the Save button disabled
  // and gives the operator a single deterministic place to read field
  // errors from.
  const payload: CreateArrivalCityInput | null = useMemo(() => {
    const trimmedName = name.trim();
    const trimmedShort = shortName.trim();
    if (!trimmedName) return null;
    if (!SHORT_NAME_REGEX.test(trimmedShort)) return null;
    return { name: trimmedName, shortName: trimmedShort, isDefault, active: true };
  }, [name, shortName, isDefault]);

  const mutation = useMutation({
    mutationFn: createArrivalCity,
    onSuccess: () => {
      // Reset so the guard sees clean baseline before close.
      setName('');
      setShortName('');
      setIsDefault(false);
      void qc.invalidateQueries({ queryKey: ARRIVAL_CITIES_QK });
      showToast({ tone: 'success', text: t('arrivalCities:toasts.created') });
      onClose();
    },
    onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
  });

  // WHY: dirty for create = trimmed name is non-empty, shortName is
  // non-empty (even if invalid — operator still wants confirm-close),
  // or the default toggle was flipped. Whitespace-only input matches
  // the canonical create baseline and must not trip the guard.
  const dirty = name.trim().length > 0 || shortName.trim().length > 0 || isDefault;

  return (
    <EditorModal
      open
      title={t('arrivalCities:modals.createTitle')}
      width={460}
      dirty={dirty}
      busy={mutation.isPending}
      onConfirmClose={onClose}
    >
      <ArrivalCityForm
        name={name}
        shortName={shortName}
        isDefault={isDefault}
        active
        onChangeName={setName}
        onChangeShortName={setShortName}
        onChangeIsDefault={setIsDefault}
      />
      <EditorFooter>
        <CancelButton onClose={onClose} label={t('common:actions.cancel')} />
        <Button
          kind="primary"
          disabled={payload === null || mutation.isPending}
          onClick={() => payload && mutation.mutate(payload)}
        >
          {t('common:actions.create')}
        </Button>
      </EditorFooter>
    </EditorModal>
  );
}

function ArrivalCityEditorModal({
  row,
  canManage,
  onClose,
  onUpdated,
}: {
  row: ArrivalCity;
  canManage: boolean;
  onClose: () => void;
  onUpdated: (row: ArrivalCity) => void;
}) {
  const { t } = useTranslation(['arrivalCities', 'common']);
  const qc = useQueryClient();
  const showToast = useToastStore((s) => s.show);
  const [name, setName] = useState(row.name);
  const [shortName, setShortName] = useState(row.shortName);
  const [isDefault, setIsDefault] = useState(row.isDefault);

  // WHY (#699): the patch object is the diff of dirty fields. When a
  // single field changes we ship the single key; mixing valid + invalid
  // returns null so the Save button stays disabled until every dirty
  // value passes its wire-schema gate.
  const patch: PatchArrivalCityInput | null = useMemo(() => {
    const trimmedName = name.trim();
    const trimmedShort = shortName.trim();
    const diff: PatchArrivalCityInput = {};
    if (trimmedName && trimmedName !== row.name) {
      diff.name = trimmedName;
    } else if (!trimmedName) {
      // Required field was cleared — block submit.
      return null;
    }
    if (trimmedShort !== row.shortName) {
      if (!SHORT_NAME_REGEX.test(trimmedShort)) return null;
      diff.shortName = trimmedShort;
    }
    if (isDefault !== row.isDefault) {
      diff.isDefault = isDefault;
    }
    return Object.keys(diff).length === 0 ? null : diff;
  }, [name, shortName, isDefault, row.name, row.shortName, row.isDefault]);

  const patchMutation = useMutation({
    mutationFn: (body: PatchArrivalCityInput) => patchArrivalCity(row.id, body),
    onSuccess: (next) => {
      // Reset baseline so the guard sees clean state on the close path.
      setName(next.name);
      setShortName(next.shortName);
      setIsDefault(next.isDefault);
      void qc.invalidateQueries({ queryKey: ARRIVAL_CITIES_QK });
      showToast({ tone: 'success', text: t('arrivalCities:toasts.updated') });
      onUpdated(next);
    },
    onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
  });

  const archiveMutation = useMutation({
    mutationFn: () => archiveArrivalCity(row.id),
    onSuccess: (next) => {
      void qc.invalidateQueries({ queryKey: ARRIVAL_CITIES_QK });
      showToast({ tone: 'success', text: t('arrivalCities:toasts.archived') });
      onUpdated(next);
    },
    onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
  });

  const activateMutation = useMutation({
    mutationFn: () => activateArrivalCity(row.id),
    onSuccess: (next) => {
      void qc.invalidateQueries({ queryKey: ARRIVAL_CITIES_QK });
      showToast({ tone: 'success', text: t('arrivalCities:toasts.activated') });
      onUpdated(next);
    },
    onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
  });

  const busy = patchMutation.isPending || archiveMutation.isPending || activateMutation.isPending;
  // WHY: dirty for edit = the diff helper above produced a non-empty
  // patch against the loaded `row`. Trim already happens in `patch`, so
  // pure-whitespace edits read as clean.
  const dirty = patch !== null;

  return (
    <EditorModal
      open
      title={t('arrivalCities:modals.editTitle', { name: row.name })}
      width={460}
      dirty={dirty}
      busy={busy}
      onConfirmClose={onClose}
    >
      <ArrivalCityForm
        name={name}
        shortName={shortName}
        isDefault={isDefault}
        active={row.active}
        readOnly={!canManage}
        onChangeName={setName}
        onChangeShortName={setShortName}
        onChangeIsDefault={setIsDefault}
      />
      <EditorFooter>
        {canManage &&
          (row.active ? (
            <Button kind="ghost" disabled={busy} onClick={() => archiveMutation.mutate()}>
              {t('common:actions.archive')}
            </Button>
          ) : (
            <Button kind="ghost" disabled={busy} onClick={() => activateMutation.mutate()}>
              {t('common:actions.restore')}
            </Button>
          ))}
        <CancelButton onClose={onClose} label={t('common:actions.close')} />
        {canManage && (
          <Button
            kind="primary"
            disabled={patch === null || busy}
            onClick={() => patch && patchMutation.mutate(patch)}
          >
            {t('common:actions.save')}
          </Button>
        )}
      </EditorFooter>
    </EditorModal>
  );
}

// WHY: Cancel must call `useEditorGuard().confirmDestructive(onClose)` to
// prompt on dirty close. The EditorGuardContext provider only wraps the
// EditorModal `children` subtree, not the `footer` prop, so the action
// buttons are rendered inline at the bottom of children. `EditorFooter`
// mirrors the visual style of the Modal footer slot.
function EditorFooter({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        marginTop: 12,
        marginLeft: -16,
        marginRight: -16,
        marginBottom: -16,
        padding: '12px 16px',
        borderTop: '1px solid var(--border-2)',
        display: 'flex',
        gap: 8,
        justifyContent: 'flex-end',
      }}
    >
      {children}
    </div>
  );
}

function CancelButton({ onClose, label }: { onClose: () => void; label: string }) {
  const { confirmDestructive } = useEditorGuard();
  return (
    <Button kind="ghost" onClick={() => confirmDestructive(onClose)}>
      {label}
    </Button>
  );
}

function ArrivalCityForm({
  name,
  shortName,
  isDefault,
  active,
  readOnly = false,
  onChangeName,
  onChangeShortName,
  onChangeIsDefault,
}: {
  name: string;
  shortName: string;
  isDefault: boolean;
  active: boolean;
  readOnly?: boolean;
  onChangeName: (value: string) => void;
  onChangeShortName: (value: string) => void;
  onChangeIsDefault: (value: boolean) => void;
}) {
  const { t } = useTranslation('arrivalCities');
  const defaultToggleId = useId();
  const trimmedShort = shortName.trim();
  // WHY (#699): empty short name suppresses the error so first-paint
  // never shouts at the operator; the Save button stays disabled by
  // the payload memo until the regex passes.
  const shortNameInvalid = trimmedShort.length > 0 && !SHORT_NAME_REGEX.test(trimmedShort);
  // WHY (#699): isDefault toggle is disabled when the row is archived;
  // a default arrival city must be selectable in the picker, so
  // archived rows cannot hold the flag. The toggle also disables under
  // read-only RBAC.
  const isDefaultDisabled = readOnly || !active;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12 }}>
        <span style={{ color: 'var(--fg-3)' }}>{t('fields.name')} *</span>
        <Input value={name} onChange={onChangeName} readOnly={readOnly} />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12 }}>
        <span style={{ color: 'var(--fg-3)' }}>{t('fields.shortName')} *</span>
        <Input
          value={shortName}
          onChange={onChangeShortName}
          readOnly={readOnly}
          aria-invalid={shortNameInvalid}
        />
        <span style={{ color: shortNameInvalid ? 'var(--status-error-fg)' : 'var(--fg-3)' }}>
          {shortNameInvalid ? t('errors.shortNameRegex') : t('fields.shortNameHelp')}
        </span>
      </label>
      <label
        htmlFor={defaultToggleId}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 12,
          color: 'var(--fg-2)',
          cursor: isDefaultDisabled ? 'default' : 'pointer',
        }}
      >
        <input
          id={defaultToggleId}
          type="checkbox"
          checked={isDefault}
          disabled={isDefaultDisabled}
          onChange={(e) => onChangeIsDefault(e.currentTarget.checked)}
        />
        {t('fields.isDefault')}
      </label>
    </div>
  );
}
